// Daily tool engagement — the red/green dot each ORDER-section tool shows on
// the SyQi home dashboard. Red at the start of the day, green once the user has
// intelligently engaged with that tool. The day's goal is all dots green.
//
// Two ways a tool goes green:
//   'action'  — the tool already has an affordance that proves engagement
//               (CLIO's entry + Save, THEMIS's checkbox, NUMA's done/reschedule,
//               MoyRi's circles, METIS/KYRS completing something). Those widgets
//               call markToolEngaged(), and a few are ALSO derived from the
//               signals the dashboard already loads (see DERIVED below) so
//               engagement that happened inside the full tool still counts.
//   'clarify' — observational tools with nothing to submit (ASTRS, KRNOS, TKNE,
//               ORACLE…). SyQi asks one question about that tool's current
//               state; answering it in the box is the engagement.
//
// Storage: public.tool_engagements, one row per (user, day, gear, tool) —
// see supabase/migrations/20260726T120000Z_tool_engagements.sql. Keyed by gear
// too, because a tool in two sections (CLIO: Orient entry / Revise close) owns
// a dot in each.
import { useCallback, useEffect, useState } from 'react';
import { Gear, TOOL_NAMES } from '../domain/olmps';
import { supabase } from '../lib/supabase';
import { dateKeyOf } from './useTodayData';
import type { UseTodayData } from './useTodayData';

export type EngagementKind = 'clarify' | 'action';
export type EngagementMode = 'native' | 'clarify';

export interface ToolEngagement {
  gear: Gear;
  toolId: string;
  kind: EngagementKind;
  question: string;
  note: string;
}

/** Composite key for a dot — a tool's engagement is per section band. */
export function engagementKey(gear: Gear, toolId: string): string {
  return `${gear}:${toolId}`;
}

// ── Which tools already prove engagement through their own UI ───────────────
// Everything NOT listed here gets the clarify box. Keep this list honest: a
// tool belongs in 'native' only if using it writes something we can see.
const NATIVE_TOOLS = new Set(['clio', 'numa', 'themis', 'metis', 'kyrs', 'moyri']);

export function engagementModeFor(toolId: string): EngagementMode {
  return NATIVE_TOOLS.has(toolId) ? 'native' : 'clarify';
}

// ── SyQi's clarifying question per tool ─────────────────────────────────────
// Contextual where the dashboard data already tells us something worth asking
// about; a plain "what do you want to do about it" otherwise.
type QuestionFn = (data: UseTodayData) => string;

const QUESTIONS: Record<string, QuestionFn> = {
  atlas: (d) =>
    d.driftSectors.length
      ? `${d.driftSectors.join(', ')} drifted off course. Where are you trying to go there — and what tells you you've arrived?`
      : 'Every sector is on course. What does today need to accomplish, and why?',
  telos: (d) =>
    d.driftSectors.length
      ? `${d.driftSectors.join(', ')} drifted off trajectory. Re-aim, or push through?`
      : 'Your sectors are holding their trajectory. Anything you want to re-aim today?',
  astrs: (d) =>
    d.driftSectors.length
      ? `The compass shows ${d.driftSectors.join(', ')} off trajectory. What do you want to do about that?`
      : 'Compass is clean today. What are you watching?',
  sofia: () => "What are you least sure about today? Name it and SyQi will hold it.",
  argos: (d) =>
    d.flo ? `Flow is at ${Math.round(d.flo.flow.score)}. What's dragging it?` : "What's pulling your attention off today?",
  hrmez: () => 'Anything you need to send, ask, or chase today?',
  krnos: (d) =>
    d.dayEntries.length
      ? `${d.dayEntries.length} things sit on today's calendar. Anything that needs moving?`
      : "Nothing is on today's calendar. Is that deliberate?",
  tkne: () => 'Which project stage are you actually moving today?',
  jnus: () => 'Any decision waiting on you? Name it and your current leaning.',
  epic: () => "What's the headline of today's episode?",
  oracle: () => "What changed since yesterday that your plan doesn't know about yet?",
  nemos: (d) =>
    d.overdueCount > 0
      ? `${d.overdueCount} things went overdue. What are you doing about them?`
      : 'Nothing slipped today. What are you protecting tomorrow?',
};

export function clarifyQuestionFor(toolId: string, data: UseTodayData): string {
  const fn = QUESTIONS[toolId];
  if (fn) return fn(data);
  return `What do you want to do about ${TOOL_NAMES[toolId] ?? toolId.toUpperCase()} today?`;
}

// ── Write path ─────────────────────────────────────────────────────────────
// A tiny module-level bus: widgets that record an 'action' engagement live far
// from the dashboard's dot row, so a write notifies every mounted reader
// instead of threading a refresh callback down through each widget.
const listeners = new Set<() => void>();

function notifyEngagementChanged() {
  for (const fn of listeners) fn();
}

/**
 * Record an engagement for today. `kind: 'action'` never overwrites an existing
 * row (a clarify answer already there keeps its text); a clarify submit does
 * update, so the user can revise their answer.
 */
export async function markToolEngaged(
  userId: string | undefined,
  gear: Gear,
  toolId: string,
  opts?: { kind?: EngagementKind; question?: string; note?: string }
): Promise<void> {
  if (!userId) return;
  const kind = opts?.kind ?? 'action';
  const row = {
    user_id: userId,
    date: dateKeyOf(new Date()),
    gear,
    tool_id: toolId,
    kind,
    question: opts?.question ?? '',
    note: opts?.note ?? '',
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from('tool_engagements')
    .upsert(row, { onConflict: 'user_id,date,gear,tool_id', ignoreDuplicates: kind === 'action' });
  if (error) {
    console.warn('[toolEngagement] write failed:', error.message);
    return;
  }
  notifyEngagementChanged();
}

// ── Read path ──────────────────────────────────────────────────────────────
// Signals from tools whose engagement is written to their OWN tables, so using
// the full tool (not just the dashboard widget) still turns the dot green.
// gear→tool pairs are resolved against whatever sections the user has active.
interface DerivedSignals {
  taskDoneToday: boolean;
  agendaDoneToday: boolean;
  journalToday: boolean;
}

function derivedEngaged(toolId: string, gear: Gear, s: DerivedSignals): boolean {
  switch (toolId) {
    // Completing a task is KYRS's and NUMA's proof of engagement.
    case 'kyrs':
    case 'numa':
      return s.taskDoneToday;
    // Ticking off an agenda item is METIS's.
    case 'metis':
      return s.agendaDoneToday;
    // The morning journal entry satisfies Orient's CLIO. Revise's CLIO is the
    // evening close — its own submit, its own dot.
    case 'clio':
      return gear === 'orient' && s.journalToday;
    default:
      return false;
  }
}

export interface ToolEngagementState {
  /** True when this tool's dot should be green today. */
  isEngaged: (gear: Gear, toolId: string) => boolean;
  /** The stored engagement row, when there is one (clarify answers). */
  engagementFor: (gear: Gear, toolId: string) => ToolEngagement | null;
  /** Submit a clarify answer — turns the dot green and stores the exchange. */
  submitClarify: (gear: Gear, toolId: string, question: string, note: string) => Promise<void>;
  loading: boolean;
}

/**
 * Today's engagement state for every tool. One query for the explicit rows plus
 * two cheap "did the user finish something today" probes for the tools that
 * record engagement in their own tables.
 */
export function useToolEngagement(userId: string | undefined, data: UseTodayData): ToolEngagementState {
  const [rows, setRows] = useState<Record<string, ToolEngagement>>({});
  const [derived, setDerived] = useState<DerivedSignals>({
    taskDoneToday: false,
    agendaDoneToday: false,
    journalToday: false,
  });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Re-read whenever any widget records an engagement.
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setRows({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    const today = dateKeyOf(new Date());
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const dayStartIso = dayStart.toISOString();

    setLoading(true);
    void Promise.all([
      supabase
        .from('tool_engagements')
        .select('gear, tool_id, kind, question, note')
        .eq('user_id', userId)
        .eq('date', today),
      supabase
        .from('tasks')
        .select('id')
        .eq('user_id', userId)
        .eq('done', true)
        .gte('updated_at', dayStartIso)
        .limit(1),
      supabase
        .from('agenda_items')
        .select('id')
        .eq('user_id', userId)
        .eq('completed', true)
        .gte('updated_at', dayStartIso)
        .limit(1),
    ]).then(([engRes, taskRes, agendaRes]) => {
      if (cancelled) return;
      if (engRes.error) console.warn('[toolEngagement] read failed:', engRes.error.message);
      const next: Record<string, ToolEngagement> = {};
      for (const r of (engRes.data ?? []) as Array<{
        gear: Gear;
        tool_id: string;
        kind: EngagementKind;
        question: string | null;
        note: string | null;
      }>) {
        next[engagementKey(r.gear, r.tool_id)] = {
          gear: r.gear,
          toolId: r.tool_id,
          kind: r.kind,
          question: r.question ?? '',
          note: r.note ?? '',
        };
      }
      setRows(next);
      setDerived({
        taskDoneToday: (taskRes.data ?? []).length > 0,
        agendaDoneToday: (agendaRes.data ?? []).length > 0,
        journalToday: data.journalWrittenToday,
      });
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, tick, data.journalWrittenToday]);

  const isEngaged = useCallback(
    (gear: Gear, toolId: string) =>
      !!rows[engagementKey(gear, toolId)] || derivedEngaged(toolId, gear, derived),
    [rows, derived]
  );

  const engagementFor = useCallback(
    (gear: Gear, toolId: string) => rows[engagementKey(gear, toolId)] ?? null,
    [rows]
  );

  const submitClarify = useCallback(
    async (gear: Gear, toolId: string, question: string, note: string) => {
      await markToolEngaged(userId, gear, toolId, { kind: 'clarify', question, note });
    },
    [userId]
  );

  return { isEngaged, engagementFor, submitClarify, loading };
}
